import {
  Dispatch,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useReactFlow } from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';
import { GraphNode } from '@morph-mapper/node-logic';
import { RFGraphEdge, RFGraphNode } from './utils';
import { useGraphContext } from '../../context';
import { DrawerKind } from '../../store/ui';

interface LayoutProps {
  layoutNodes: RFGraphNode[];
  layoutEdges: RFGraphEdge[];
  setNodes: Dispatch<SetStateAction<RFGraphNode[]>>;
}

type NodeSize = {
  width: number;
  height: number;
};

type Position = {
  x: number;
  y: number;
};

const elk = new ELK();
const POSITION_EPSILON = 0.5;
const SIZE_EPSILON = 1;
const PANEL_WIDTH = 350;
const CENTER_DURATION_MS = 250;
const FIT_VIEW_OPTIONS = {
  padding: 0.05,
  includeHiddenNodes: true,
  duration: CENTER_DURATION_MS,
};

const round = (n: number, precision = 1) =>
  Math.round(n * 10 ** precision) / 10 ** precision;

const byId = <T extends { id: string }>(left: T, right: T) =>
  left.id.localeCompare(right.id);

const getNodeSize = (node: RFGraphNode): NodeSize => ({
  width: node.measured?.width ?? node.width ?? 0,
  height: node.measured?.height ?? node.height ?? 0,
});

const isNodeMeasured = (node: RFGraphNode) =>
  Boolean(node.measured?.width && node.measured?.height);

const positionsEqual = (
  left: Position,
  right: Position,
  epsilon = POSITION_EPSILON
) => {
  return (
    Math.abs(left.x - right.x) <= epsilon && Math.abs(left.y - right.y) <= epsilon
  );
};

const sizesChanged = (
  previous: Map<string, NodeSize> | null,
  next: Map<string, NodeSize>,
  epsilon = SIZE_EPSILON
) => {
  if (!previous) return true;
  if (previous.size !== next.size) return true;

  for (const [id, nextSize] of next) {
    const previousSize = previous.get(id);
    if (!previousSize) return true;

    const widthDelta = Math.abs(round(nextSize.width) - round(previousSize.width));
    const heightDelta = Math.abs(
      round(nextSize.height) - round(previousSize.height)
    );
    if (widthDelta > epsilon || heightDelta > epsilon) {
      return true;
    }
  }

  return false;
};

const buildSizeMap = (nodes: RFGraphNode[]) =>
  new Map(nodes.map((node) => [node.id, getNodeSize(node)]));

const generateSizeKey = (nodes: RFGraphNode[]) =>
  [...nodes]
    .sort(byId)
    .map((node) => {
      const size = getNodeSize(node);
      return `${node.id}:${Math.round(size.width)}x${Math.round(size.height)}`;
    })
    .join('|');

const generateTopologyKey = (nodes: RFGraphNode[], edges: RFGraphEdge[]) => {
  const nodeIds = [...nodes].sort(byId).map((node) => node.id);
  const edgeTopology = [...edges]
    .sort(byId)
    .map(
      (edge) =>
        `${edge.id}:${edge.source}:${edge.sourceHandle ?? ''}->${edge.target}:${
          edge.targetHandle ?? ''
        }:${(edge.data?.order as number) ?? 0}`
    );

  return `${nodeIds.join(',')}__${edgeTopology.join(',')}`;
};

const byEdgeOrder = (a: RFGraphEdge, b: RFGraphEdge) =>
  ((a.data?.order as number) ?? 0) - ((b.data?.order as number) ?? 0);

const getElkChildren = (nodes: RFGraphNode[], edges: RFGraphEdge[]) => {
  const nodeSet = new Set(nodes.map((n) => n.id));
  const parentOf = new Map<string, string>();
  const edgesByParent = new Map<string, RFGraphEdge[]>();

  for (const edge of edges) {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) continue;
    parentOf.set(edge.source, edge.target);
    const group = edgesByParent.get(edge.target) ?? [];
    group.push(edge);
    edgesByParent.set(edge.target, group);
  }

  const childrenOf = new Map<string, string[]>();
  for (const [parentId, group] of edgesByParent) {
    childrenOf.set(
      parentId,
      [...group].sort(byEdgeOrder).map((e) => e.source)
    );
  }

  const rootId = nodes.find((n) => !parentOf.has(n.id))?.id;
  const order = new Map<string, number>();
  let counter = 0;
  const queue: string[] = rootId ? [rootId] : [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (order.has(id)) continue;
    order.set(id, counter++);
    for (const childId of childrenOf.get(id) ?? []) {
      queue.push(childId);
    }
  }

  return [...nodes]
    .sort((a, b) => (order.get(a.id) ?? Infinity) - (order.get(b.id) ?? Infinity))
    .map((node) => {
      const size = getNodeSize(node);
      // ELK INTERACTIVE strategy needs y-hints to honour BFS order.
      const y = (order.get(node.id) ?? 0) * 200;
      return { id: node.id, width: size.width, height: size.height, x: 0, y };
    });
};

const getElkEdges = (edges: RFGraphEdge[]) =>
  [...edges].sort(byEdgeOrder).map((edge) => ({
    id: edge.id,
    sources: [edge.source],
    targets: [edge.target],
  }));

const getLayoutedPositions = async (
  nodes: RFGraphNode[],
  edges: RFGraphEdge[]
) => {
  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.layered.spacing.nodeNodeBetweenLayers': '180',
      'elk.spacing.nodeNode': '80',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.edgeNode': '50',
      'elk.layered.mergeEdges': 'false',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.layered.crossingMinimization.strategy': 'INTERACTIVE',
    },
    children: getElkChildren(nodes, edges),
    edges: getElkEdges(edges),
  };

  const layout = await elk.layout(graph);
  const layoutById = new Map(
    (layout.children ?? []).map((child) => [
      child.id,
      { x: child.x ?? 0, y: child.y ?? 0 },
    ])
  );

  return new Map(
    nodes.map((node) => [node.id, layoutById.get(node.id) ?? node.position])
  );
};

const hasPositionChanges = (
  nodes: RFGraphNode[],
  nextPositions: Map<string, Position>
) =>
  nodes.some((node) => {
    const nextPosition = nextPositions.get(node.id);
    if (!nextPosition) return false;
    return !positionsEqual(node.position, nextPosition);
  });

const mergeNodePositions = (
  nodes: RFGraphNode[],
  nextPositions: Map<string, Position>
) => {
  let didChange = false;
  const nextNodes = nodes.map((node) => {
    const nextPosition = nextPositions.get(node.id);
    if (!nextPosition || positionsEqual(node.position, nextPosition)) {
      return node;
    }

    didChange = true;
    return {
      ...node,
      position: nextPosition,
    };
  });

  return didChange ? nextNodes : nodes;
};

export const useLayout = ({
  setNodes,
  layoutNodes: nodes,
  layoutEdges: edges,
}: LayoutProps) => {
  const drawer = useGraphContext(({ ui }) => ui.drawer);
  const focusNodeId = useGraphContext(({ ui }) => ui.focusNodeId);
  const setNodePositions = useGraphContext(({ ui }) => ui.setNodePositions);
  const setLayoutRunning = useGraphContext(({ ui }) => ui.setLayoutRunning);
  const { fitView, setCenter, getNode, getZoom, getViewport, setViewport } =
    useReactFlow();

  const isLayoutReady = useMemo(() => nodes.every(isNodeMeasured), [nodes]);
  const topologyKey = useMemo(
    () => generateTopologyKey(nodes, edges),
    [nodes, edges]
  );
  const sizeKey = useMemo(() => generateSizeKey(nodes), [nodes]);
  const sizeMap = useMemo(() => buildSizeMap(nodes), [nodes]);

  const layoutRunIdRef = useRef(0);
  const shouldCenterAfterLayoutRef = useRef(false);
  const lastObservedTopologyKeyRef = useRef<string | null>(null);
  const lastScheduledTopologyKeyRef = useRef<string | null>(null);
  const lastScheduledLayoutKeyRef = useRef<string | null>(null);
  const lastSizeMapRef = useRef<Map<string, NodeSize> | null>(null);
  const lastFocusedNodeIdRef = useRef<string | null>(null);
  const wasNodeConfigOpenRef = useRef(false);
  const hasLaidOutOnceRef = useRef(false);
  const stableNodeCountRef = useRef<number | null>(null);
  const cascadeStartNodeCountRef = useRef<number | null>(null);
  const layoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusNodeIdRef = useRef<string | null>(null);
  const pendingViewportCompensationRef = useRef<{
    x: number;
    y: number;
    zoom: number;
  } | null>(null);

  const focusNodeForConfig = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId);
      if (!node) return;

      const zoom = getZoom() || 1;
      const nodeWidth = node.measured?.width ?? node.width ?? 0;
      const nodeHeight = node.measured?.height ?? node.height ?? 0;
      const centerX = node.position.x + nodeWidth / 2 + PANEL_WIDTH / 2 / zoom;
      const centerY = node.position.y + nodeHeight / 2;

      setCenter(centerX, centerY, { zoom, duration: CENTER_DURATION_MS });
    },
    [getNode, getZoom, setCenter]
  );

  const centerViewport = useCallback(() => {
    if (drawer.kind === DrawerKind.NodeConfig) {
      const nodeId = drawer.nodeId;
      if (nodeId && lastFocusedNodeIdRef.current !== nodeId) {
        focusNodeForConfig(nodeId);
        lastFocusedNodeIdRef.current = nodeId;
      }
      wasNodeConfigOpenRef.current = true;
      return;
    }

    if (drawer.kind === DrawerKind.NodePicker) {
      wasNodeConfigOpenRef.current = false;
      return;
    }

    if (wasNodeConfigOpenRef.current) {
      const nodeId = lastFocusedNodeIdRef.current;
      const node = nodeId ? getNode(nodeId) : undefined;
      wasNodeConfigOpenRef.current = false;
      lastFocusedNodeIdRef.current = null;
      if (node) {
        const zoom = getZoom() || 1;
        const nodeWidth = node.measured?.width ?? node.width ?? 0;
        const nodeHeight = node.measured?.height ?? node.height ?? 0;
        setCenter(
          node.position.x + nodeWidth / 2,
          node.position.y + nodeHeight / 2,
          { zoom, duration: CENTER_DURATION_MS }
        );
        return;
      }
    }

    fitView(FIT_VIEW_OPTIONS);
  }, [drawer, fitView, focusNodeForConfig, getNode, getZoom, setCenter]);

  const runLayout = useCallback(
    async (sourceNodes: RFGraphNode[], sourceEdges: RFGraphEdge[]) => {
      setLayoutRunning(true);
      const runId = ++layoutRunIdRef.current;
      const nextPositions = await getLayoutedPositions(sourceNodes, sourceEdges);

      if (runId !== layoutRunIdRef.current) {
        setLayoutRunning(false);
        return;
      }
      if (!hasPositionChanges(sourceNodes, nextPositions)) {
        setLayoutRunning(false);
        return;
      }

      const isInitialLayout = !hasLaidOutOnceRef.current;
      hasLaidOutOnceRef.current = true;

      // Multiple runLayout calls can cascade from one topology change (measurements
      // settle over frames) — cascadeStartNodeCountRef is frozen per topology so
      // every call in the cascade agrees on whether this is a start-graph transition.
      const previousNodeCount = cascadeStartNodeCountRef.current;
      const isStartGraphTransition =
        isInitialLayout ||
        (previousNodeCount !== null &&
          (previousNodeCount <= 1 || sourceNodes.length <= 1));

      const anchorNode =
        sourceNodes.find((node) => node.id === focusNodeIdRef.current) ??
        sourceNodes.find((node) => node.data?.kind === GraphNode.Terminal);
      const anchorPrevPosition = anchorNode?.position;
      const anchorNextPosition = anchorNode
        ? nextPositions.get(anchorNode.id)
        : undefined;

      setNodePositions(
        Object.fromEntries(
          sourceNodes.map((node) => [
            node.id,
            nextPositions.get(node.id) ?? node.position,
          ])
        )
      );
      setNodes((currentNodes) => mergeNodePositions(currentNodes, nextPositions));

      if (isStartGraphTransition) {
        shouldCenterAfterLayoutRef.current = true;
      } else if (anchorPrevPosition && anchorNextPosition) {
        const dx = anchorNextPosition.x - anchorPrevPosition.x;
        const dy = anchorNextPosition.y - anchorPrevPosition.y;

        if (Math.abs(dx) > POSITION_EPSILON || Math.abs(dy) > POSITION_EPSILON) {
          const viewport = getViewport();
          // Defer to useLayoutEffect so the viewport shift and node reposition land in the same paint.
          pendingViewportCompensationRef.current = {
            x: viewport.x - dx * viewport.zoom,
            y: viewport.y - dy * viewport.zoom,
            zoom: viewport.zoom,
          };
        }
      }

      setLayoutRunning(false);
    },
    [getViewport, setLayoutRunning, setNodePositions, setNodes, setViewport]
  );

  useLayoutEffect(() => {
    if (lastObservedTopologyKeyRef.current === topologyKey) {
      return;
    }

    lastObservedTopologyKeyRef.current = topologyKey;
    layoutRunIdRef.current += 1;

    cascadeStartNodeCountRef.current = stableNodeCountRef.current;
    stableNodeCountRef.current = nodes.length;
  }, [nodes.length, topologyKey]);

  useLayoutEffect(() => {
    focusNodeIdRef.current = focusNodeId;
  }, [focusNodeId]);

  useLayoutEffect(() => {
    if (!isLayoutReady) {
      return;
    }

    const layoutKey = `${topologyKey}|${sizeKey}`;
    const topologyChanged = lastScheduledTopologyKeyRef.current !== topologyKey;
    const didSizeChange = sizesChanged(lastSizeMapRef.current, sizeMap);

    if (!topologyChanged && lastScheduledLayoutKeyRef.current === layoutKey) {
      return;
    }
    if (!topologyChanged && !didSizeChange) {
      return;
    }

    lastScheduledTopologyKeyRef.current = topologyKey;
    lastScheduledLayoutKeyRef.current = layoutKey;
    lastSizeMapRef.current = sizeMap;

    // Debounce to collapse ELK jitter (pixel-level position oscillations while measurements settle).
    if (layoutTimeoutRef.current) {
      clearTimeout(layoutTimeoutRef.current);
    }
    layoutTimeoutRef.current = setTimeout(() => {
      layoutTimeoutRef.current = null;
      runLayout(nodes, edges);
    }, 50);

    return () => {
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
        layoutTimeoutRef.current = null;
      }
    };
  }, [edges, isLayoutReady, nodes, runLayout, sizeKey, sizeMap, topologyKey]);

  useLayoutEffect(() => {
    if (!shouldCenterAfterLayoutRef.current || !isLayoutReady) {
      return;
    }

    shouldCenterAfterLayoutRef.current = false;
    // Double rAF: React Flow's node store needs an extra frame to pick up new positions before fitView.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        centerViewport();
      });
    });
  }, [centerViewport, isLayoutReady, nodes, topologyKey]);

  useLayoutEffect(() => {
    if (!isLayoutReady) {
      return;
    }

    if (drawer.kind !== DrawerKind.NodeConfig && !wasNodeConfigOpenRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      centerViewport();
    });
  }, [centerViewport, drawer, isLayoutReady, topologyKey]);

  useLayoutEffect(() => {
    const compensation = pendingViewportCompensationRef.current;
    if (!compensation) return;
    pendingViewportCompensationRef.current = null;
    setViewport(compensation, { duration: 0 });
  });
};
